import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';

import { SignInService } from './sign-in.service';
import { TotpValidateComponent } from './totp-validate/totp-validate.component';
import { TotpSetupComponent } from '../sign-up/totp-setup/totp-setup.component';
import { SidenavService } from '../../shared/sidenav/sidenav.service';
import { NotificationService } from '../settings/services/notification.service';
import { ThemeService } from '../settings/services/theme.service';
import { UserModel } from '../user.model';
import { normalizeSettings } from '../settings/services/user-settings.service';

@Component({
  selector: 'app-sign-in',
  standalone: false,
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.css'
})
export class SignInComponent {

  email = '';
  password = '';
  errorMessage: string = '';
  statusMessage = '';
  // From the click on Sign-In until the attempt fails or is cancelled; blocks a second TOTP dialog
  isSigningIn = false;
  // Sign-Up button hidden on the sign-in page; the /account/sign-up page still works
  showSignUp = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private signInService: SignInService,
    private sidenavService: SidenavService,
    private themeService: ThemeService,
    private notificationService: NotificationService
  ) { }

  async onSubmit() {
    // Enter or a second click while an attempt is running
    if (this.isSigningIn) return;

    this.isSigningIn = true;
    this.errorMessage = '';
    this.statusMessage = '';

    try {
      const result = await this.signInService.signIn(this.email, this.password);
      if (result.status === 'signed-in') {
        await this.finishSignIn(result.user);
        return;
      }
      const enrollment = result.status === 'enrollment-required';
      this.openTotpDialog(enrollment, enrollment ? result.otpauthUrl : undefined);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Não foi possível entrar.';
      this.isSigningIn = false;
    }
  }

  // Settings of the user and the page that asked for the sign-in
  private async finishSignIn(user: UserModel): Promise<void> {
    const settings = user.settings ? normalizeSettings(user.settings) : null;

    if (settings) {
      this.themeService.themeModeChange(settings.theme);
      this.sidenavService.sidenavStatusChange(settings.sidenav.status);
      this.sidenavService.sidenavModeChange(settings.sidenav.type);
      this.sidenavService.sidenavSizeChange(settings.sidenav.size);
      this.notificationService.notificationModeChange(settings.notification);
    }

    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    await this.router.navigateByUrl(returnUrl?.startsWith('/') ? returnUrl : '/home');
  }

  private openTotpDialog(enrollment: boolean, otpauthUrl?: string): void {
    const dialogRef = enrollment
      // autoFocus off: the dialogs focus the first digit of the code themselves (focus-otp.ts)
      ? this.dialog.open(TotpSetupComponent, { disableClose: true, autoFocus: false, data: { otpauthUrl } })
      : this.dialog.open(TotpValidateComponent, { disableClose: true, autoFocus: false });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result?.code) {
        try {
          await this.signInService.cancelPendingSignIn();
        } finally {
          this.isSigningIn = false;
        }
        return;
      }

      try {
        if (enrollment) {
          await this.signInService.completeTotpEnrollment(result.code);
          this.statusMessage = 'Autenticador configurado. Entre novamente para concluir a autenticação.';
          this.isSigningIn = false;
          return;
        }

        const user = await this.signInService.completeTotpSignIn(result.code, result.rememberDevice === true);
        await this.finishSignIn(user);
      } catch (error) {
        this.errorMessage = error instanceof Error ? error.message : 'Não foi possível validar o autenticador.';
        try {
          await this.signInService.cancelPendingSignIn();
        } finally {
          this.isSigningIn = false;
        }
      }
    });
  }
}
